import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ShieldCheck, ShieldAlert, Clock, ArrowRight, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function KYCBanner() {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/kyc/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setStatus(data); })
      .catch(() => {});
  }, [token]);

  if (dismissed || !status) return null;
  if (status.kyc_status === 'approved') return null;

  const configs = {
    not_started: {
      bg: 'bg-blue-500/5 border-blue-500/15',
      icon: Shield,
      iconColor: 'text-blue-400',
      text: 'Complete identity verification to access all platform features.',
      cta: 'Start Verification',
    },
    pending: {
      bg: 'bg-yellow-500/5 border-yellow-500/15',
      icon: Clock,
      iconColor: 'text-yellow-400',
      text: 'Your identity verification is under review.',
      cta: 'View Status',
    },
    rejected: {
      bg: 'bg-red-500/5 border-red-500/15',
      icon: ShieldAlert,
      iconColor: 'text-red-400',
      text: 'Identity verification was not approved. Please review and resubmit.',
      cta: 'View Details',
    },
    retry: {
      bg: 'bg-orange-500/5 border-orange-500/15',
      icon: ShieldAlert,
      iconColor: 'text-orange-400',
      text: 'Additional documents are needed for verification.',
      cta: 'Resubmit',
    },
  };

  const config = configs[status.kyc_status] || configs.not_started;
  const Icon = config.icon;

  return (
    <div className={`relative flex items-center gap-3 p-3 px-4 rounded-xl border ${config.bg} mb-5`} data-testid="kyc-banner">
      <Icon className={`w-5 h-5 ${config.iconColor} flex-shrink-0`} />
      <p className="text-sm text-gray-300 flex-1">{config.text}</p>
      <Link
        to="/verification"
        className="flex items-center gap-1 text-sm font-medium text-blue-400 hover:text-blue-300 whitespace-nowrap"
        data-testid="kyc-banner-cta"
      >
        {config.cta} <ArrowRight className="w-3.5 h-3.5" />
      </Link>
      <button
        onClick={() => setDismissed(true)}
        className="text-gray-600 hover:text-gray-400 ml-1"
        data-testid="kyc-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
